import React, { useEffect, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { toast } from "react-toastify";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import RightworkAutocomplete from "../../../../components/shared/RightworkAutocomplete";
import RightworkDatePicker from "../../../../components/shared/RightworkDatePicker";
import { applyAutofill } from "./assetFormat";
import { getRightworkAPI, postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_get_maintenance_asset_suggestions,
  API_post_maintenance_asset,
  API_update_maintenance_asset,
} from "../../../../constants";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

const EMPTY_ASSET = {
  name: "",
  code: "",
  location: "",
  manufacturer: "",
  model_number: "",
  serial_number: "",
  install_date: "",
  purchase_cost: "",
  warranty_expires: "",
  vendor: "",
  notes: "",
};

// Fields a known model fills in on the asset form.
const MODEL_FILLED_FIELDS = ["manufacturer", "name", "vendor", "purchase_cost"];
// Fields a known name fills in.
const NAME_FILLED_FIELDS = ["manufacturer", "model_number", "location", "vendor", "purchase_cost"];
const EMPTY_SUGGESTIONS = { names: [], areas: [], vendors: [], makes: [], model_defaults: [], name_defaults: [] };
const sameText = (a, b) => (a || "").trim().replace(/\s+/g, " ").toLowerCase() === (b || "").trim().replace(/\s+/g, " ").toLowerCase();

// A text field that also offers what's already been entered at the company's
// other locations. Typing something new is always allowed. The closed field
// matches the drawer's other inputs; the menu is Rightwork's branded one.
function SuggestField(props) {
  return <RightworkAutocomplete {...props} fieldSx={textFieldSx} />;
}

function Field({ label, action, children }) {
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{label}</p>
        {action}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Add or edit one asset. ``asset`` null means add; ``storeId`` is the store
// the new asset belongs to.
function AssetDrawer({ isOpen, onClose, asset, homeStore, storeId, onSaved }) {
  const isEdit = Boolean(asset);
  const [form, setForm] = useState(EMPTY_ASSET);
  const [isSaving, setIsSaving] = useState(false);
  const [suggestions, setSuggestions] = useState(EMPTY_SUGGESTIONS);
  // Values a pick filled in, by field: { value, source } where source is
  // "name" or "model". A later pick from the same source may replace them;
  // anything the user typed, or the other source filled, is left alone.
  const autofilled = useRef({});
  // The name/model each fill last ran for: leaving a field without changing
  // it (or a value a pick just filled in) shouldn't run the fill again.
  const committed = useRef({});

  // What other locations have entered, for the pick-lists and model auto-fill.
  useEffect(() => {
    if (!isOpen || !homeStore) return;
    let cancelled = false;
    getRightworkAPI(API_get_maintenance_asset_suggestions, { store_id: homeStore, asset_store_id: storeId || homeStore })
      .then((res) => {
        if (!cancelled) setSuggestions({ ...EMPTY_SUGGESTIONS, ...(res.data?.redux?.payload || {}) });
      })
      .catch(() => {
        // Suggestions are a convenience; the form works without them.
        if (!cancelled) setSuggestions(EMPTY_SUGGESTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, homeStore, storeId]);

  const makeOptions = suggestions.makes.map((m) => m.manufacturer).filter(Boolean);
  const matchingMake = suggestions.makes.find((m) => m.manufacturer && sameText(m.manufacturer, form.manufacturer));
  const modelOptions = matchingMake
    ? matchingMake.models
    : [...new Set(suggestions.makes.flatMap((m) => m.models))];

  // Picking a model that another location already has (or finishing typing
  // one) fills the make, the usual name, service vendor, and purchase cost,
  // but only into empty fields. Not on each keystroke, so "ABC" isn't
  // applied on the way to typing a new "ABC-2".
  // See applyAutofill for which fields a pick may change.
  const fillFrom = (base, known, fields, source) => {
    const { next, autofilled: filled } = applyAutofill(base, known, fields, source, autofilled.current);
    autofilled.current = filled;
    committed.current = { ...committed.current, name: next.name, model_number: next.model_number };
    setForm(next);
  };

  const fillFromModel = (value) => {
    if (sameText(committed.current.model_number, value)) return;
    const sameModel = suggestions.model_defaults.filter((d) => sameText(d.model_number, value));
    const makeTyped = form.manufacturer.trim() && form.manufacturer !== autofilled.current.manufacturer?.value;
    // Prefer the make in the form; a make an earlier pick filled in may give
    // way to the new model's.
    let candidates = form.manufacturer.trim()
      ? sameModel.filter((d) => sameText(d.manufacturer, form.manufacturer))
      : sameModel;
    if (!candidates.length && !makeTyped) candidates = sameModel;
    // A model number two makes share can't be told apart without the make.
    const known = candidates.length === 1 ? candidates[0] : null;
    fillFrom({ ...form, model_number: value }, known, MODEL_FILLED_FIELDS, "model");
  };

  // A name another location uses (a 36-inch griddle's) brings its make,
  // model, area, vendor and cost, when that name always means one make/model.
  const fillFromName = (value) => {
    if (sameText(committed.current.name, value)) return;
    const known = suggestions.name_defaults.find((d) => sameText(d.name_key, value)) || null;
    fillFrom({ ...form, name: value }, known, NAME_FILLED_FIELDS, "name");
  };
  const setField = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!isOpen) return;
    autofilled.current = {};
    committed.current = {};
    if (asset) {
      const next = { ...EMPTY_ASSET };
      Object.keys(EMPTY_ASSET).forEach((key) => {
        next[key] = asset[key] ?? "";
      });
      // The saved name and model count as already chosen, so tabbing through
      // an edit doesn't fill blank fields from company defaults.
      committed.current = { name: next.name, model_number: next.model_number };
      setForm(next);
    } else {
      setForm(EMPTY_ASSET);
    }
  }, [isOpen, asset]);

  // The date picker only reports complete dates, so an optional date is
  // emptied with a Clear link, as on the ticket drawer.
  const clearLink = (key) =>
    form[key] ? (
      <button type="button" className="text-xs text-violet-700 hover:underline" onClick={() => setField(key)("")}>
        Clear
      </button>
    ) : null;

  const bind = (key) => ({
    value: form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Asset name is required");
      return;
    }
    if (isEdit && !form.code.trim()) {
      toast.error("Asset code is required");
      return;
    }
    setIsSaving(true);
    try {
      const { code, ...fields } = form;
      const payload = { store_id: homeStore, asset_store_id: storeId, ...fields };
      const res = isEdit
        ? await postRightworkAPI(API_update_maintenance_asset, { ...payload, code, asset_uuid: asset.uuid })
        : await postRightworkAPI(API_post_maintenance_asset, payload);
      toast.success(isEdit ? "Asset updated" : "Asset added");
      onSaved?.(res.data?.redux?.payload?.maintenance_asset);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.system?.message || "Failed to save asset");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      <div className="w-[450px] h-full bg-slate-50 flex flex-col">
        <div className="flex-1 overflow-auto">
          <DrawerHeader title={isEdit ? "Edit Asset" : "Add Asset"} onClose={onClose} />
          <div className="px-6 pb-10">
            <Field label="Name *">
              <SuggestField
                value={form.name}
                onChange={setField("name")}
                onCommit={fillFromName}
                options={suggestions.names}
                placeholder={'e.g. 48" griddle, Line reach-in cooler 1'}
                ariaLabel="Name"
              />
            </Field>
            {/* New assets always get the next code automatically; it can be
                renamed here afterwards (or brought in through CSV import). */}
            <div className="flex gap-3">
              {isEdit && (
                <div className="flex-1">
                  <Field label="Sticker code *">
                    <TextField
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 20, "aria-label": "Sticker code" }}
                      sx={textFieldSx}
                      {...bind("code")}
                    />
                  </Field>
                </div>
              )}
              <div className="flex-1">
                <Field label="Area">
                  <SuggestField
                    value={form.location}
                    onChange={setField("location")}
                    options={suggestions.areas}
                    placeholder="e.g. Line, Walk-in, Bar"
                    ariaLabel="Area"
                  />
                </Field>
              </div>
            </div>

            <p className="mt-8 text-sm font-semibold text-slate-700">Data plate</p>
            <Field label="Manufacturer">
              <SuggestField value={form.manufacturer} onChange={setField("manufacturer")} options={makeOptions} ariaLabel="Manufacturer" />
            </Field>
            <Field label="Model number">
              <SuggestField
                value={form.model_number}
                onChange={setField("model_number")}
                onCommit={fillFromModel}
                options={modelOptions}
                ariaLabel="Model number"
              />
            </Field>
            <Field label="Serial number">
              <TextField fullWidth size="small" inputProps={{ maxLength: 255, "aria-label": "Serial number" }} sx={textFieldSx} {...bind("serial_number")} />
            </Field>

            <p className="mt-8 text-sm font-semibold text-slate-700">Purchase and warranty</p>
            <div className="flex gap-3">
              <div className="flex-1">
                <Field label="Installed" action={clearLink("install_date")}>
                  <RightworkDatePicker fullWidth height={40} label="Install date" value={form.install_date || null} onChange={setField("install_date")} />
                </Field>
              </div>
              <div className="flex-1">
                <Field label="Warranty until" action={clearLink("warranty_expires")}>
                  <RightworkDatePicker fullWidth height={40} label="Warranty until" value={form.warranty_expires || null} onChange={setField("warranty_expires")} />
                </Field>
              </div>
            </div>
            <Field label="Purchase cost">
              <TextField
                fullWidth
                size="small"
                inputProps={{ inputMode: "decimal", "aria-label": "Purchase cost" }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={textFieldSx}
                {...bind("purchase_cost")}
              />
            </Field>
            {/* Full width: vendor names run long ("Coastal Restaurant Service"). */}
            <Field label="Service vendor">
              <SuggestField value={form.vendor} onChange={setField("vendor")} options={suggestions.vendors} ariaLabel="Service vendor" />
            </Field>
            <Field label="Notes">
              <TextField fullWidth size="small" multiline minRows={3} inputProps={{ maxLength: 5000, "aria-label": "Notes" }} sx={textFieldSx} {...bind("notes")} />
            </Field>
          </div>
        </div>
        <div className="border-t border-slate-200 bg-white px-6 py-4 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSave}
            className="flex-1 py-3 rounded-xl bg-violet-700 hover:bg-violet-800 text-white font-medium disabled:opacity-60"
          >
            {isSaving ? "Saving..." : isEdit ? "Save Asset" : "Add Asset"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

export default AssetDrawer;
